/**
 * The wallet (ADR 0013, DESIGN §10.1): gems and Marks, with a reason-coded local ledger of the newest entries and the
 * record of real-money purchases granted, by store transaction id. Pure and deterministic: every change is given its
 * time. Gems come from play (D20: star chests, the weekly chest, the check-in's gem days, boss first clears, achievements,
 * events) and from gem packs; Marks only from play (the Trading Post, events, Proving Grounds, the Trial, Heroic and farm
 * wins) and are never sold. Valor, the Event shop's own currency, merged into Marks on 2026-09-30 (docs/SHOP.md §9.2;
 * older saves convert on load, app saves.ts v6). Trophies (MP-23, docs/SHOP.md §9.7) are earned like Marks, only in the
 * hardest weekly fights (balance/src/trophies.json `_sources`), spent in the Trophy Hall and never sold; a save from
 * before them holds 0.
 */

/** Gems, Marks and Trophies. */
export type Currency = "gems" | "marks" | "trophies";
/** Every currency the wallet holds. */
export const CURRENCIES: readonly Currency[] = ["gems", "marks", "trophies"];
/** How a refusal names each currency ("not enough Marks"). */
const CURRENCY_WORDS: Readonly<Record<Currency, string>> = { gems: "gems", marks: "Marks", trophies: "Trophies" };

/** Why a balance changed: every grant and spend carries one, so the ledger and a support request can say where gems went. */
export const WALLET_REASONS = [
  // gem sources (D20) and the Store
  "starChest", "weeklyChest", "checkin", "bossFirstClear", "achievement", "event", "purchase", "refund",
  // the Deals screen (docs/SHOP.md): an offer's gems, a free weekly-track milestone's
  "deal", "dealTrack",
  // gem sinks
  "builder", "speedUpPack", "resourcePack", "crate", "blackMarket", "skin", "finishNow",
  // MP-06: pre-battle supplies bought in the Shop; Pets v2: a Pet Treat
  "supplies", "petTreat",
  // owner 2026-10-21: a battle boost (meta/boosts.ts)
  "boost",
  // Marks
  "tradeSell", "tradeBuy",
  // the currency shops (docs/SHOP.md §9): spends in the Merchant and the Event shop (Marks), the VIP shop (gems), a
  // Merchant refresh (gems); Marks earned from Proving Grounds, the Trial, Heroic and farm wins ("eventPlay"; logged as
  // "valor" before Valor merged into Marks, rewritten by the save migration)
  "merchant", "eventShop", "vipShop", "merchantRefresh", "eventPlay",
  // the Starfall Wyrm (M40, docs/RARES.md): another try after it flew off (gems), the complete set's gems on a kill
  "wyrmRetry", "wyrm",
  // MP-23: a Trophy Hall purchase (a cosmetic or the Armoury's weekly shelf), paid in Trophies
  "trophyHall",
  "debug",
] as const;
export type WalletReason = (typeof WALLET_REASONS)[number];

export interface LedgerEntry {
  /** Unix seconds. */
  readonly at: number;
  readonly currency: Currency;
  /** Signed change. */
  readonly delta: number;
  readonly reason: WalletReason;
  /** What it was about: a product id, a level, a transaction id… ("" for none). */
  readonly ref: string;
}

/** A real-money purchase as granted (ADR 0013: once per store transaction). */
export interface PurchaseRecord {
  readonly transactionId: string;
  readonly productId: string;
  /** Gems granted for it (0 when the refund arrived before the grant). */
  readonly gems: number;
  readonly at: number;
  /** The store refunded or revoked it. */
  revoked: boolean;
  /** Gems taken back on the refund: what was still unspent, at most `gems`. */
  takenBack: number;
}

export interface WalletJson {
  gems: number; marks: number; ledger: LedgerEntry[]; purchases: PurchaseRecord[];
  /** MP-23: absent in a save from before the Trophy Hall (then 0). */
  trophies?: number;
  /** MP-13: the newest signed store date seen (Unix seconds); absent in older saves (then the purchases' dates). */
  signedFloor?: number;
}

/** A store transaction id we accept: StoreKit's are decimal strings, the sandbox store's short tokens. */
const TRANSACTION_ID = /^[A-Za-z0-9._:-]{1,128}$/;

const isAmount = (n: unknown): n is number => Number.isInteger(n) && (n as number) > 0;
const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0;

export class Wallet {
  gems = 0;
  marks = 0;
  /** MP-23: earned only (never sold), spent in the Trophy Hall. */
  trophies = 0;
  /** Newest last; at most `ledgerSize` entries. */
  readonly ledger: LedgerEntry[] = [];
  private readonly purchases: PurchaseRecord[] = [];
  /** The latest time the wallet was given, for grants whose caller has no clock (a star chest claimed from the map). */
  private lastAt = 0;
  /**
   * MP-13: the newest date a verified store transaction carried (its signed purchase or revocation date), Unix seconds.
   * The device clock can be moved back; this cannot, so "now" for claim windows is at least this (`trustedNow`).
   */
  signedFloor = 0;

  constructor(readonly ledgerSize: number) {}

  balance(currency: Currency): number { return currency === "gems" ? this.gems : currency === "marks" ? this.marks : this.trophies; }
  canAfford(currency: Currency, amount: number): boolean { return isCount(amount) && this.balance(currency) >= amount; }

  /** Adds a positive whole amount. Returns false (changing nothing) for anything else. `at` defaults to the last known time. */
  grant(currency: Currency, amount: number, reason: WalletReason, at?: number, ref = ""): boolean {
    if (!isAmount(amount)) return false;
    this.set(currency, this.balance(currency) + amount);
    this.note(currency, amount, reason, at, ref);
    return true;
  }

  /** Gems for an achievement, an event node, a boss… (the hook later features pay through). */
  grantGems(amount: number, reason: WalletReason, at?: number, ref = ""): boolean { return this.grant("gems", amount, reason, at, ref); }

  /** Takes a positive whole amount the wallet holds; the reason why not otherwise. Free (0) spends succeed and are not logged. */
  spend(currency: Currency, amount: number, reason: WalletReason, at?: number, ref = ""): true | string {
    if (!isCount(amount)) return "bad amount";
    if (this.balance(currency) < amount) return `not enough ${CURRENCY_WORDS[currency]}`;
    if (amount === 0) return true;
    this.set(currency, this.balance(currency) - amount);
    this.note(currency, -amount, reason, at, ref);
    return true;
  }

  // ------------------------------------------------------------------ real-money purchases (Store port)

  purchase(transactionId: string): PurchaseRecord | null { return this.purchases.find((p) => p.transactionId === transactionId) ?? null; }

  /** Raises the signed-date floor to a verified transaction's own date (seconds); a malformed or older date changes nothing. */
  noteSignedDate(atSeconds: number): void {
    if (Number.isFinite(atSeconds) && atSeconds > this.signedFloor) this.signedFloor = Math.floor(atSeconds);
  }

  /** The time to judge claim windows by (MP-13): the device clock, never earlier than the newest signed store date. */
  trustedNow(deviceNow: number): number { return Math.max(deviceNow, this.signedFloor); }

  /**
   * Adds gems a purchase paid later (a card's daily claim, a fund milestone: meta/cards.ts) to its record, so a refund
   * takes back what is still unspent of everything it paid. False for an unknown or refunded transaction.
   */
  addPurchaseGems(transactionId: string, gems: number): boolean {
    const i = this.purchases.findIndex((p) => p.transactionId === transactionId);
    if (i < 0 || this.purchases[i].revoked || !isAmount(gems)) return false;
    this.purchases[i] = { ...this.purchases[i], gems: this.purchases[i].gems + gems };
    return true;
  }
  get purchaseCount(): number { return this.purchases.length; }

  /**
   * Grants a verified purchase once: false, granting nothing, when the transaction was already recorded (granted,
   * or refunded before its grant) or its id or amount is malformed.
   */
  /** Whether `productId` was ever bought on this save (refunded or not): a gem pack's first-purchase bonus is once. */
  boughtBefore(productId: string): boolean { return this.purchases.some((p) => p.productId === productId); }

  grantPurchase(transactionId: string, productId: string, gems: number, at: number): boolean {
    if (!TRANSACTION_ID.test(transactionId) || !isAmount(gems) || this.purchase(transactionId) !== null) return false;
    this.purchases.push({ transactionId, productId, gems, at, revoked: false, takenBack: 0 });
    this.noteSignedDate(at);
    this.grant("gems", gems, "purchase", at, productId);
    return true;
  }

  /**
   * Records a verified Deals purchase (docs/SHOP.md §4.2) whose items the Deals rules paid themselves (gems with reason
   * `deal`): `gems` is what it paid in gems, so a refund takes back at most that. False, recording nothing, when the
   * transaction is already recorded (granted, or refunded before its grant) or malformed.
   */
  recordPurchase(transactionId: string, productId: string, gems: number, at: number): boolean {
    if (!TRANSACTION_ID.test(transactionId) || !isCount(gems) || this.purchase(transactionId) !== null) return false;
    this.purchases.push({ transactionId, productId, gems, at, revoked: false, takenBack: 0 });
    this.noteSignedDate(at);
    return true;
  }

  /**
   * A refund (ADR 0013): takes back what is still unspent of the transaction's gems, never below zero, once. A refund
   * of a transaction never granted is recorded, so a late delivery of it grants nothing. Returns the gems taken back.
   */
  revokePurchase(transactionId: string, productId: string, at: number): number {
    if (!TRANSACTION_ID.test(transactionId)) return 0;
    const p = this.purchase(transactionId);
    if (p === null) { this.purchases.push({ transactionId, productId, gems: 0, at, revoked: true, takenBack: 0 }); return 0; }
    if (p.revoked) return 0;
    p.revoked = true;
    p.takenBack = Math.min(p.gems, this.gems);
    if (p.takenBack > 0) { this.gems -= p.takenBack; this.note("gems", -p.takenBack, "refund", at, productId); }
    return p.takenBack;
  }

  private set(currency: Currency, v: number): void {
    if (currency === "gems") this.gems = v;
    else if (currency === "marks") this.marks = v;
    else this.trophies = v;
  }

  private note(currency: Currency, delta: number, reason: WalletReason, at: number | undefined, ref: string): void {
    const when = at !== undefined && Number.isFinite(at) ? at : this.lastAt;
    this.lastAt = Math.max(this.lastAt, when);
    this.ledger.push({ at: when, currency, delta, reason, ref });
    if (this.ledger.length > this.ledgerSize) this.ledger.splice(0, this.ledger.length - this.ledgerSize);
  }

  toJson(): WalletJson {
    return { gems: this.gems, marks: this.marks, trophies: this.trophies, ledger: this.ledger.map((e) => ({ ...e })), purchases: this.purchases.map((p) => ({ ...p })), signedFloor: this.signedFloor };
  }

  /**
   * Restores a wallet field by field: a malformed entry is dropped, a malformed balance reads 0. Lenient on purpose —
   * the section holds bought gems, so one bad ledger line must not cost the whole wallet.
   */
  static fromJson(ledgerSize: number, j: unknown): Wallet {
    const w = new Wallet(ledgerSize);
    if (typeof j !== "object" || j === null) return w;
    const src = j as Partial<WalletJson>;
    if (isCount(src.gems)) w.gems = src.gems;
    if (isCount(src.marks)) w.marks = src.marks;
    if (isCount(src.trophies)) w.trophies = src.trophies;
    if (Array.isArray(src.ledger)) for (const e of src.ledger.filter(validEntry).slice(-ledgerSize)) w.ledger.push({ ...e });
    if (Array.isArray(src.purchases)) for (const p of src.purchases) if (validPurchase(p) && w.purchase(p.transactionId) === null) w.purchases.push({ ...p });
    w.lastAt = Math.max(0, ...w.ledger.map((e) => e.at), ...w.purchases.map((p) => p.at));
    w.signedFloor = signedFloorOf(src);
    return w;
  }
}

/**
 * The signed-date floor a wallet section holds (MP-13): its `signedFloor`, and for a save from before it the newest
 * granted (not refunded) purchase's date. 0 for none or anything malformed.
 */
export function signedFloorOf(j: unknown): number {
  if (typeof j !== "object" || j === null) return 0;
  const src = j as Partial<WalletJson>;
  const own = Number.isFinite(src.signedFloor) && (src.signedFloor as number) > 0 ? Math.floor(src.signedFloor as number) : 0;
  const bought = Array.isArray(src.purchases) ? src.purchases.filter((p) => validPurchase(p) && !p.revoked).map((p) => Math.floor(p.at)) : [];
  return Math.max(0, own, ...bought);
}

function validEntry(e: unknown): e is LedgerEntry {
  const x = e as LedgerEntry;
  return typeof x === "object" && x !== null && Number.isFinite(x.at) && (CURRENCIES as readonly string[]).includes(x.currency) && Number.isInteger(x.delta)
    && (WALLET_REASONS as readonly string[]).includes(x.reason) && typeof x.ref === "string";
}

function validPurchase(p: unknown): p is PurchaseRecord {
  const x = p as PurchaseRecord;
  return typeof x === "object" && x !== null && typeof x.transactionId === "string" && TRANSACTION_ID.test(x.transactionId) && typeof x.productId === "string"
    && isCount(x.gems) && Number.isFinite(x.at) && typeof x.revoked === "boolean" && isCount(x.takenBack) && x.takenBack <= x.gems;
}
